import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractions } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import usePairAddress from './usePairAddress';
import useGenericCall from './useGenericCall';
import { useOffchainPoolToken } from './useOffchain';
import useFactory from './useFactory';
import { PoolTokenType, FactoryIndex, Address } from 'impermax-sdk';


export default function useClaims(): [ButtonState, () => Promise<void>] {
  const factory = useFactory()
  const pairAddress = usePairAddress()
  const onchainInteractions = useOnchainInteractions();
  const symbol = useGenericCall(useOffchainPoolToken(PoolTokenType.Collateral), poolToken => poolToken.getSymbol());
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const summary = `Claim IBEX reward for ${symbol}`;
  
  const claimsState: ButtonState = useMemo(() => {
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [pending]);

  const claims = useCallback(async (): Promise<void> => {
    if (claimsState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractions.claims({[factory]: [pairAddress]}, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [claimsState, factory, pairAddress, onchainInteractions, summary, addTransaction]);

  return [claimsState, claims];
}

export function useClaimsAll(pairsToClaim: FactoryIndex<Address[]>): [ButtonState, () => Promise<void>] {
  const onchainInteractions = useOnchainInteractions();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const summary = `Harvest all`;
  
  const claimsState: ButtonState = useMemo(() => {
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [pending]);

  const claims = useCallback(async (): Promise<void> => {
    if (claimsState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractions.claims(pairsToClaim, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [claimsState, onchainInteractions, pairsToClaim, summary, addTransaction]);

  return [claimsState, claims];
}